import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Confetti from "@/components/Confetti";
import { starterScansPhrase, planNumbers } from "@/lib/planCopy";

const TID = {
  root: "payment-success-root",
  successState: "payment-success-state",
  failureState: "payment-failure-state",
};

const MAX_POLLS = 20;
const POLL_INTERVAL_MS = 2500;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshWorkspace } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | success | expired | error
  const [pollCount, setPollCount] = useState(0);
  const [error, setError] = useState(null);

  const poll = useCallback(async () => {
    if (!sessionId) {
      setState("error");
      setError("Missing session_id in URL.");
      return;
    }
    try {
      const resp = await api.get(`/payments/stripe/status/${encodeURIComponent(sessionId)}`);
      const data = resp.data;
      if (data.status === "paid" || data.workspace_upgraded) {
        await refreshWorkspace();
        setState("success");
        toast.success("🎉 Congratulations! You're on Starter — everything's unlocked.");
        return;
      }
      if (data.status === "expired") {
        setState("expired");
        return;
      }
      setPollCount((c) => c + 1);
    } catch (e) {
      setError(e?.response?.data?.detail || "Payment status check failed.");
      setState("error");
    }
  }, [sessionId, refreshWorkspace]);

  useEffect(() => {
    if (state !== "polling") return;
    if (pollCount >= MAX_POLLS) {
      setError("Payment is taking longer than expected. Please check your email or contact support.");
      setState("error");
      return;
    }
    const id = setTimeout(poll, pollCount === 0 ? 400 : POLL_INTERVAL_MS);
    return () => clearTimeout(id);
  }, [pollCount, poll, state]);

  return (
    <div data-testid={TID.root} className="min-h-screen bg-lm-bg flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {state === "polling" && (
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-lm-tint2 flex items-center justify-center mx-auto mb-6 relative">
              <Loader2 className="w-8 h-8 text-lm-primary animate-spin" />
            </div>
            <h1 className="text-2xl font-display font-semibold mb-2">Confirming your payment…</h1>
            <p className="text-lm-text2 leading-relaxed">
              Stripe is finalizing the transaction. This usually takes a few seconds.
            </p>
          </div>
        )}

        {state === "success" && (
          <div data-testid={TID.successState} className="text-center bg-lm-surface border border-lm-border rounded-2xl p-8 shadow-lg">
            <Confetti />
            <div className="w-16 h-16 rounded-2xl bg-lm-primary text-white flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8" strokeWidth={2} />
            </div>
            <h1 className="text-3xl font-display font-semibold mb-2">🎉 You're on Starter!</h1>
            <p className="text-lm-text2 leading-relaxed mb-7">
              Payment confirmed. You've unlocked <strong>{starterScansPhrase()}</strong> (up to {planNumbers().starterKeywords} keywords each),
              plus source links, saving leads and all lenses.
            </p>
            <button
              onClick={() => navigate("/app/find-customers", { replace: true })}
              className="w-full inline-flex items-center justify-center gap-2 bg-lm-primary hover:bg-lm-primaryHover text-white rounded-lg px-5 py-3.5 font-semibold"
            >
              Back to Find Customers
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {state === "expired" && (
          <div className="text-center bg-lm-surface border border-lm-border rounded-2xl p-8">
            <div className="w-16 h-16 rounded-2xl bg-lm-tint text-lm-text2 flex items-center justify-center mx-auto mb-5">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-display font-semibold mb-2">Session expired</h1>
            <p className="text-lm-text2 mb-6">Your checkout session expired. No charge was made.</p>
            <Link to="/app/billing" className="text-lm-primary font-medium hover:underline">Try again</Link>
          </div>
        )}

        {state === "error" && (
          <div data-testid={TID.failureState} className="text-center bg-lm-surface border border-red-200 rounded-2xl p-8">
            <div className="w-16 h-16 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-5">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-display font-semibold mb-2">Payment check failed</h1>
            <p className="text-lm-text2 mb-6">{error}</p>
            <div className="flex flex-col gap-2">
              <Link to="/app/billing" className="text-lm-primary font-medium hover:underline">Go back to billing</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
